import { useState, type FormEvent } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { ArrowLeft, Copy, LogIn, Power, ShieldCheck, UserPlus } from 'lucide-react';
import { adminApi } from '../../lib/api-client';
import { useAuth } from '../../lib/auth-context';
import { PageHeader } from '../../components/layout/PageHeader';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input, Label } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogTrigger } from '../../components/ui/dialog';
import { LoadingState, ErrorState, EmptyState, friendlyErrorMessage } from '../../components/ui/states';
import { useToast } from '../../components/ui/toast';
import { RUN_STATUS_VARIANT } from '../../lib/run-status';
import type { Role } from '../../lib/types';

const ROLE_LABEL: Record<Role, string> = { owner: 'Owner', admin: 'Admin', operator: 'Operator', viewer: 'Viewer' };

function ProvisionUserForm({ workspaceId, onProvisioned }: { workspaceId: string; onProvisioned: (tempPassword: string) => void }) {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [role, setRole] = useState<Role>('operator');
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: () => adminApi.provisionUser(workspaceId, { email, name, role }),
    onSuccess: (result) => onProvisioned(result.temporaryPassword),
    onError: (err) => setError(friendlyErrorMessage(err)),
  });

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    mutation.mutate();
  };

  return (
    <form className="space-y-4" onSubmit={handleSubmit}>
      <div className="space-y-1">
        <Label htmlFor="provision-email">Email</Label>
        <Input id="provision-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="name@customer.com" required />
      </div>
      <div className="space-y-1">
        <Label htmlFor="provision-name">Full name</Label>
        <Input id="provision-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Jane Doe" required />
      </div>
      <div className="space-y-1">
        <Label htmlFor="provision-role">Role</Label>
        <Select value={role} onValueChange={(v) => setRole(v as Role)}>
          <SelectTrigger id="provision-role">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {(Object.entries(ROLE_LABEL) as Array<[Role, string]>).map(([value, label]) => (
              <SelectItem key={value} value={value}>
                {label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      {error ? <p className="text-xs text-danger">{error}</p> : null}
      <Button type="submit" className="w-full" disabled={mutation.isPending || !email.trim() || !name.trim()}>
        Provision user
      </Button>
    </form>
  );
}

function RevealTempPassword({ password, onDone }: { password: string; onDone: () => void }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    await navigator.clipboard.writeText(password);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <div className="space-y-4">
      <p className="text-sm font-medium text-danger">Share this securely — it will not be shown again.</p>
      <div className="flex items-center justify-between gap-2 rounded-xl border border-border bg-surface-sunken px-3 py-2">
        <code className="truncate text-sm text-ink">{password}</code>
        <Button variant="ghost" size="sm" onClick={copy}>
          <Copy size={14} /> {copied ? 'Copied!' : 'Copy'}
        </Button>
      </div>
      <Button className="w-full" onClick={onDone}>
        Done
      </Button>
    </div>
  );
}

export function WorkspaceDetail() {
  const { id } = useParams<{ id: string }>();
  const workspaceId = id!;
  const { toast } = useToast();
  const navigate = useNavigate();
  const { impersonate } = useAuth();
  const [provisionOpen, setProvisionOpen] = useState(false);
  const [revealedPassword, setRevealedPassword] = useState<string | null>(null);

  const query = useQuery({ queryKey: ['admin', 'workspace', workspaceId], queryFn: () => adminApi.getWorkspace(workspaceId) });

  const impersonateMutation = useMutation({
    mutationFn: () => adminApi.impersonate(workspaceId),
    onSuccess: (result) => {
      impersonate({ accessToken: result.accessToken, tenantId: result.tenantId, userId: '', role: result.role, workspaceName: result.tenantName });
      navigate('/', { replace: true });
    },
    onError: (err) => toast({ title: 'Could not switch into that workspace', description: friendlyErrorMessage(err), variant: 'error' }),
  });

  if (query.isLoading) return <LoadingState />;
  if (query.isError) return <ErrorState error={query.error} onRetry={() => query.refetch()} />;

  const { workspace, recentRuns, activeAgents, killSwitch } = query.data!;

  return (
    <div>
      <Link to="/admin" className="mb-4 inline-flex items-center gap-1.5 text-sm font-medium text-ink-muted hover:text-ink">
        <ArrowLeft size={14} /> Back to Admin Console
      </Link>

      <PageHeader
        title={workspace.name}
        description={`Workspace ${workspace.slug} · created ${new Date(workspace.createdAt).toLocaleDateString()}`}
        action={
          <Button onClick={() => impersonateMutation.mutate()} disabled={impersonateMutation.isPending}>
            <LogIn size={16} /> Switch Into Workspace
          </Button>
        }
      />

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardContent className="pt-6">
            <p className="text-sm text-ink-muted">Users</p>
            <p className="text-card-value font-bold text-ink">{workspace.userCount}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <p className="text-sm text-ink-muted">Targets</p>
            <p className="text-card-value font-bold text-ink">{workspace.targetCount}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <p className="text-sm text-ink-muted">Open findings (crit/high)</p>
            <p className="text-card-value font-bold text-ink">{workspace.openFindingsCount}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="flex items-start gap-3 pt-6">
            <Power size={18} className={killSwitch.halted ? 'text-danger' : 'text-success'} />
            <div>
              <p className="text-sm text-ink-muted">Kill switch</p>
              <Badge variant={killSwitch.halted ? 'danger' : 'success'}>{killSwitch.halted ? 'Halted' : 'Active'}</Badge>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-3">
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle>Recent scans</CardTitle>
          </CardHeader>
          <CardContent>
            {recentRuns.length === 0 ? (
              <EmptyState title="No scans yet" description="This workspace hasn't run a scan." />
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border text-left text-xs uppercase tracking-wide text-ink-faint">
                    <th className="pb-2 pr-4 font-medium">Status</th>
                    <th className="pb-2 pr-4 font-medium">Started</th>
                    <th className="pb-2 pr-4 font-medium">Progress</th>
                  </tr>
                </thead>
                <tbody>
                  {recentRuns.map((run) => (
                    <tr key={run.id} className="border-b border-border last:border-0">
                      <td className="py-2 pr-4">
                        <Badge variant={RUN_STATUS_VARIANT[run.status]} className="capitalize">
                          {run.status}
                        </Badge>
                      </td>
                      <td className="py-2 pr-4 text-ink-muted">{new Date(run.createdAt).toLocaleString()}</td>
                      <td className="py-2 pr-4 text-ink-muted">{run.progressPct}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Active agents</CardTitle>
          </CardHeader>
          <CardContent>
            {activeAgents.length === 0 ? (
              <EmptyState icon={<ShieldCheck size={24} />} title="No connected agents" />
            ) : (
              <ul className="space-y-2">
                {activeAgents.map((agent) => (
                  <li key={agent.id} className="flex items-center justify-between gap-2 text-sm">
                    <span className="truncate font-medium text-ink">{agent.hostname}</span>
                    <Badge variant="success">Connected</Badge>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      </div>

      <Card className="mt-4">
        <CardHeader>
          <CardTitle>Users</CardTitle>
        </CardHeader>
        <CardContent className="flex items-center justify-between gap-4">
          <p className="text-sm text-ink-muted">Provision a new member directly into this workspace, with a temporary password.</p>
          <Dialog
            open={provisionOpen}
            onOpenChange={(open) => {
              setProvisionOpen(open);
              if (!open) setRevealedPassword(null);
            }}
          >
            <DialogTrigger asChild>
              <Button variant="outline">
                <UserPlus size={16} /> Provision New User
              </Button>
            </DialogTrigger>
            <DialogContent title={revealedPassword ? 'User provisioned' : 'Provision a new user'}>
              {revealedPassword ? (
                <RevealTempPassword
                  password={revealedPassword}
                  onDone={() => {
                    setProvisionOpen(false);
                    setRevealedPassword(null);
                  }}
                />
              ) : (
                <ProvisionUserForm workspaceId={workspace.id} onProvisioned={setRevealedPassword} />
              )}
            </DialogContent>
          </Dialog>
        </CardContent>
      </Card>
    </div>
  );
}
